import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemedStyles } from '../../theme/useThemedStyles';
import { ModalBackdrop } from '../common/ModalBackdrop';
import { BolivianDateInput } from '../common/BolivianDateInput';

export interface TreatmentStep {
  name: string;
  description?: string;
  cost: string;
}

export interface TreatmentForm {
  name: string;
  cost: string;
  recommendedPrice: string;
  additionalUseFee: string;
  doctorId: string;
  isMultiStep: boolean;
  steps: TreatmentStep[];
  installments: { dueDate: string; amount: string }[];
  createdBy?: string;
}

interface Doctor { id: number; firstName: string; lastName: string }

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (form: any) => Promise<void> | void;
  initial?: Partial<TreatmentForm>;
  doctors: Doctor[];
  isEditing?: boolean;
}

const modalOverlay: React.CSSProperties = { 
  position: 'fixed', 
  inset: 0, 
  background: 'rgba(0,0,0,0.7)', 
  display: 'flex', 
  alignItems: 'center', 
  justifyContent: 'center', 
  zIndex: 1000 
};

const modalStyle: React.CSSProperties = { 
  borderRadius: 12, 
  padding: 24, 
  width: 'min(900px, 95vw)', 
  maxHeight: '90vh', 
  overflow: 'auto'
};

const input: React.CSSProperties = { 
  padding: 10, 
  borderRadius: 6, 
  fontSize: 14
};

const btn: React.CSSProperties = { 
  padding: '10px 16px', 
  border: 'none', 
  borderRadius: 6, 
  cursor: 'pointer', 
  background: '#667eea', 
  color: '#fff', 
  fontWeight: 600 
};

const labelStyle: React.CSSProperties = { 
  fontSize: 12, 
  fontWeight: 600, 
  textTransform: 'uppercase', 
  letterSpacing: 0.5, 
  marginBottom: 6 
};

const padDatePart = (value: number) => String(value).padStart(2, '0');

export const formatLocalDate = (date: Date) =>
  `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;

export const addOneCalendarMonth = (value: string, fallbackDate = new Date()) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const parsedYear = match ? Number(match[1]) : fallbackDate.getFullYear();
  const parsedMonth = match ? Number(match[2]) : fallbackDate.getMonth() + 1;
  const parsedDay = match ? Number(match[3]) : fallbackDate.getDate();
  const sourceIsValid = Boolean(match)
    && parsedMonth >= 1
    && parsedMonth <= 12
    && parsedDay >= 1
    && new Date(parsedYear, parsedMonth, 0).getDate() >= parsedDay;
  const year = sourceIsValid ? parsedYear : fallbackDate.getFullYear();
  const month = sourceIsValid ? parsedMonth : fallbackDate.getMonth() + 1;
  const day = sourceIsValid ? parsedDay : fallbackDate.getDate();
  const targetMonth = new Date(year, month, 1);
  const lastTargetDay = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0).getDate();

  return `${targetMonth.getFullYear()}-${padDatePart(targetMonth.getMonth() + 1)}-${padDatePart(Math.min(day, lastTargetDay))}`;
};

export const EnhancedTreatmentModal: React.FC<Props> = ({ open, onClose, onSubmit, initial, doctors, isEditing }) => {
  const { t } = useTranslation();
  const theme = useThemedStyles();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [form, setForm] = useState<TreatmentForm>({ 
    name: initial?.name || '', 
    cost: initial?.cost || '0', 
    recommendedPrice: initial?.recommendedPrice || '0',
    additionalUseFee: initial?.additionalUseFee || '0',
    doctorId: initial?.doctorId || '', 
    isMultiStep: initial?.isMultiStep || false,
    steps: initial?.steps || [],
    installments: initial?.installments || [],
    createdBy: initial?.createdBy || ''
  });
  
  React.useEffect(() => {
    if (open) {
      setSubmitting(false);
      setSubmitError('');
    }
  }, [open]);

  React.useEffect(() => {
    if (open && initial) {
      setForm({
        name: initial.name || '',
        cost: initial.cost || '0',
        recommendedPrice: initial.recommendedPrice || '0',
        additionalUseFee: initial.additionalUseFee || '0',
        doctorId: initial.doctorId || '',
        isMultiStep: initial.isMultiStep || false,
        steps: initial.steps || [],
        installments: initial.installments || [],
        createdBy: initial.createdBy || ''
      });
    } else if (open && !initial) {
      setForm({ 
        name: '', 
        cost: '0', 
        recommendedPrice: '0',
        additionalUseFee: '0',
        doctorId: '', 
        isMultiStep: false,
        steps: [],
        installments: [],
        createdBy: ''
      });
    }
  }, [open, initial]);

  const totalStepCosts = useMemo(() => 
    form.steps.reduce((s, step) => s + (parseFloat(step.cost) || 0), 0), 
    [form.steps]
  );

  const totalInstallments = useMemo(() => 
    form.installments.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0), 
    [form.installments]
  );

  const calculatedCost = form.isMultiStep ? totalStepCosts : parseFloat(form.cost || '0');

  if (!open) return null;
  const themedModalStyle: React.CSSProperties = { ...modalStyle, background: theme.surface, color: theme.text, border: `1px solid ${theme.border}` };
  const themedInput: React.CSSProperties = { ...input, ...theme.input };
  const themedBtn: React.CSSProperties = { ...btn, ...theme.button };
  const themedLabel: React.CSSProperties = { ...labelStyle, color: theme.muted };

  const addStep = () => {
    setForm(f => ({ 
      ...f, 
      steps: [...f.steps, { name: '', description: '', cost: '0' }] 
    }));
  };

  const removeStep = (idx: number) => {
    setForm(f => ({ 
      ...f, 
      steps: f.steps.filter((_, i) => i !== idx) 
    }));
  };

  const updateStep = (idx: number, patch: Partial<TreatmentStep>) => {
    setForm(f => ({ 
      ...f, 
      steps: f.steps.map((step, i) => i === idx ? { ...step, ...patch } : step) 
    }));
  };

  const addInstallment = () => {
    setForm(f => {
      const previous = f.installments[f.installments.length - 1];
      const installment = previous
        ? { dueDate: addOneCalendarMonth(previous.dueDate), amount: previous.amount }
        : { dueDate: formatLocalDate(new Date()), amount: '' };

      return { ...f, installments: [...f.installments, installment] };
    });
  };

  const removeInstallment = (idx: number) => {
    setForm(f => ({ 
      ...f, 
      installments: f.installments.filter((_, i) => i !== idx) 
    }));
  };

  const updateInstallment = (idx: number, patch: any) => {
    setForm(f => ({ 
      ...f, 
      installments: f.installments.map((it, i) => i === idx ? { ...it, ...patch } : it) 
    }));
  };

  const autoSplit = (n: number) => {
    const count = Math.max(1, Math.min(36, n));
    const total = calculatedCost;
    const per = Math.floor((total / count) * 100) / 100;
    const result: any[] = [];
    let sum = 0;
    for (let i = 0; i < count; i++) {
      const date = new Date();
      date.setMonth(date.getMonth() + i);
      const amt = i === count - 1 ? (Math.round((total - sum) * 100) / 100).toFixed(2) : per.toFixed(2);
      sum += parseFloat(amt);
      result.push({ dueDate: date.toISOString().slice(0,10), amount: amt });
    }
    setForm(f => ({ ...f, installments: result }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    
    if (form.isMultiStep && form.steps.length === 0) {
      alert(t('treatmentsUi.multiStepMustHaveStep'));
      return;
    }

    const payload: any = {
      name: form.name.trim(),
      cost: form.isMultiStep ? 0 : parseFloat(form.cost),
      recommendedPrice: parseFloat(form.recommendedPrice) || 0,
      additionalUseFee: parseFloat(form.additionalUseFee) || 0,
      doctorId: form.doctorId ? parseInt(form.doctorId, 10) : null,
      isMultiStep: form.isMultiStep,
      createdBy: form.createdBy || undefined
    };

    if (form.isMultiStep) {
      payload.steps = form.steps.map(s => ({
        name: s.name.trim(),
        description: s.description?.trim() || null,
        cost: parseFloat(s.cost) || 0,
        createdBy: form.createdBy || undefined
      }));
    }

    if (form.installments.length > 0) {
      payload.installments = form.installments.map(i => ({ 
        dueDate: i.dueDate, 
        amount: parseFloat(i.amount) || 0 
      }));
    }

    setSubmitting(true);
    setSubmitError('');
    try {
      await onSubmit(payload);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t('treatmentsUi.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalBackdrop className="responsive-row" style={modalOverlay} onDismiss={onClose} dismissDisabled={submitting}>
      <div style={themedModalStyle} onClick={e => e.stopPropagation()}>
        <h2 style={{ marginTop: 0, marginBottom: 24, fontSize: 20, letterSpacing: 0.5 }}>
          {isEditing ? t('treatmentsUi.updateTreatment') : t('treatmentsUi.createNewTreatment')}
        </h2>
        
        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 20 }}>
          {/* Basic Info */}
          <div style={{ borderBottom: `1px solid ${theme.border}`, paddingBottom: 16 }}>
            <div style={themedLabel}>{t('treatmentsUi.treatmentInformation')}</div>
            <div className="responsive-grid" style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr 1fr' }}>
              <div>
                <label style={themedLabel}>{t('treatmentsUi.name')} *</label>
                <input 
                  placeholder={t('treatmentsUi.treatmentName')} 
                  style={themedInput} 
                  value={form.name} 
                  onChange={e => setForm({ ...form, name: e.target.value })} 
                  required 
                />
              </div>
              <div>
                <label style={themedLabel}>{t('treatmentsUi.doctor')}</label>
                <select 
                  style={themedInput} 
                  value={form.doctorId} 
                  onChange={e => setForm({ ...form, doctorId: e.target.value })}
                >
                  <option value="">{t('treatmentsUi.notAssigned')}</option>
                  {doctors.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.firstName} {d.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={themedLabel}>{t('treatmentsUi.treatmentType')}</label>
                <div className="responsive-row" style={{ display: 'flex', gap: 12, alignItems: 'center', height: 40 }}>
                  <label className="responsive-row" style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input 
                      type="radio" 
                      checked={!form.isMultiStep} 
                      onChange={() => setForm({ ...form, isMultiStep: false })} 
                      style={{ accentColor: theme.primary }}
                    />
                    <span style={{ fontSize: 13 }}>{t('treatmentsUi.simple')}</span>
                  </label>
                  <label className="responsive-row" style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input 
                      type="radio" 
                      checked={form.isMultiStep} 
                      onChange={() => setForm({ ...form, isMultiStep: true })} 
                      style={{ accentColor: theme.primary }}
                    />
                    <span style={{ fontSize: 13 }}>{t('treatmentsUi.multiStep')}</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="responsive-grid" style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr 1fr', marginTop: 12 }}>
              <div>
                <label style={themedLabel}>{t('treatmentsUi.recommendedPrice')}</label>
                <input
                  placeholder="0.00"
                  type="number"
                  min="0"
                  step="0.01"
                  style={themedInput}
                  value={form.recommendedPrice}
                  onChange={e => setForm({ ...form, recommendedPrice: e.target.value })}
                />
              </div>
              <div>
                <label style={themedLabel}>{t('treatmentsUi.additionalUseFee')}</label>
                <input
                  placeholder="0.00"
                  type="number"
                  min="0"
                  step="0.01"
                  style={themedInput}
                  value={form.additionalUseFee}
                  onChange={e => setForm({ ...form, additionalUseFee: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Simple Treatment Cost */}
          {!form.isMultiStep && (
            <div style={{ borderBottom: `1px solid ${theme.border}`, paddingBottom: 16 }}>
              <div style={themedLabel}>{t('treatmentsUi.cost')}</div>
              <div className="responsive-grid" style={{ display: 'grid', gap: 8, gridTemplateColumns: '200px auto' }}>
                <input 
                  placeholder="0.00" 
                  type="number" 
                  min="0" 
                  step="0.01" 
                  style={themedInput} 
                  value={form.cost} 
                  onChange={e => setForm({ ...form, cost: e.target.value })} 
                  required 
                />
                <div className="responsive-row" style={{ fontSize: 12, color: theme.muted, display: 'flex', alignItems: 'center' }}>
                  {t('treatmentsUi.total')}: Bs {parseFloat(form.cost || '0').toFixed(2)}
                </div>
              </div>
            </div>
          )}

          {/* Multi-Step Treatment Steps */}
          {form.isMultiStep && (
            <div style={{ borderBottom: `1px solid ${theme.border}`, paddingBottom: 16 }}>
              <div className="responsive-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={themedLabel}>{t('treatmentsUi.treatmentSteps')}</div>
                <button 
                  type="button" 
                  onClick={addStep} 
                  style={{ ...themedBtn, background: '#10b981', padding: '6px 12px', fontSize: 12 }}
                >
                  + {t('treatmentsUi.addStep')}
                </button>
              </div>
              
              <div style={{ display: 'grid', gap: 12 }}>
                {form.steps.length === 0 ? (
                  <div style={{ padding: 12, background: theme.inputBg, borderRadius: 6, color: theme.muted, textAlign: 'center' }}>
                    {t('treatmentsUi.noStepsStart')}
                  </div>
                ) : (
                  form.steps.map((step, idx) => (
                    <div key={idx} style={{ background: theme.inputBg, padding: 12, borderRadius: 6, display: 'grid', gap: 10 }}>
                      <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'start' }}>
                        <div>
                          <label style={{ ...themedLabel, marginBottom: 4 }}>{t('treatmentsUi.stepName', { number: idx + 1 })}</label>
                          <input 
                            placeholder="e.g. Tooth Preparation" 
                            style={themedInput} 
                            value={step.name} 
                            onChange={e => updateStep(idx, { name: e.target.value })} 
                            required
                          />
                        </div>
                        <div>
                          <label style={{ ...themedLabel, marginBottom: 4 }}>{t('treatmentsUi.cost')}</label>
                          <input 
                            placeholder="0.00" 
                            type="number" 
                            min="0" 
                            step="0.01" 
                            style={themedInput} 
                            value={step.cost} 
                            onChange={e => updateStep(idx, { cost: e.target.value })} 
                          />
                        </div>
                        <div className="responsive-row" style={{ display: 'flex', alignItems: 'flex-end' }}>
                          <button 
                            type="button" 
                            onClick={() => removeStep(idx)} 
                            style={{ ...themedBtn, background: '#ef4444', padding: '6px 8px', fontSize: 12 }}
                          >
                            {t('treatmentsUi.remove')}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label style={{ ...themedLabel, marginBottom: 4 }}>{t('treatmentsUi.descriptionOptional')}</label>
                        <input 
                          placeholder="Details about this step..." 
                          style={themedInput} 
                          value={step.description || ''} 
                          onChange={e => updateStep(idx, { description: e.target.value })} 
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>

              {form.steps.length > 0 && (
                <div className="responsive-row" style={{ marginTop: 12, padding: 12, background: theme.inputBg, borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, color: theme.muted }}>
                    {t('treatmentsUi.totalSteps')}: <strong>{form.steps.length}</strong>
                  </div>
                  <div style={{ fontSize: 13, color: theme.muted }}>
                    {t('treatmentsUi.totalCost')}: <strong style={{ color: '#10b981' }}>Bs {totalStepCosts.toFixed(2)}</strong>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Payment Schedule */}
          <div style={{ borderBottom: `1px solid ${theme.border}`, paddingBottom: 16 }}>
            <div className="responsive-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={themedLabel}>{t('treatmentsUi.paymentScheduleOptional')}</div>
              <div className="responsive-row" style={{ display: 'flex', gap: 6 }}>
                {calculatedCost > 0 && (
                  <>
                    <button 
                      type="button" 
                      onClick={() => autoSplit(2)} 
                      style={{ ...themedBtn, background: '#0ea5e9', padding: '4px 8px', fontSize: 11 }}
                    >
                      Auto 2
                    </button>
                    <button 
                      type="button" 
                      onClick={() => autoSplit(3)} 
                      style={{ ...themedBtn, background: '#0ea5e9', padding: '4px 8px', fontSize: 11 }}
                    >
                      Auto 3
                    </button>
                    <button 
                      type="button" 
                      onClick={() => autoSplit(6)} 
                      style={{ ...themedBtn, background: '#0ea5e9', padding: '4px 8px', fontSize: 11 }}
                    >
                      Auto 6
                    </button>
                  </>
                )}
                <button 
                  type="button" 
                  onClick={addInstallment} 
                  style={{ ...themedBtn, background: '#10b981', padding: '4px 8px', fontSize: 11 }}
                >
                  + {t('treatmentsUi.addPayment')}
                </button>
              </div>
            </div>

            {form.installments.length === 0 ? (
              <div style={{ padding: 12, background: theme.inputBg, borderRadius: 6, color: theme.muted, fontSize: 13, textAlign: 'center' }}>
                {t('treatmentsUi.noPaymentSchedule')}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {form.installments.map((ins, idx) => (
                  <div className="responsive-grid" key={idx} style={{ display: 'grid', gridTemplateColumns: '180px 1fr 120px', gap: 8, alignItems: 'center' }}>
                    <BolivianDateInput style={themedInput} value={ins.dueDate} onChange={dueDate => updateInstallment(idx, { dueDate })} />
                    <input 
                      placeholder={t('treatmentsUi.amount')} 
                      type="number" 
                      min="0" 
                      step="0.01" 
                      style={themedInput} 
                      value={ins.amount} 
                      onChange={e => updateInstallment(idx, { amount: e.target.value })} 
                    />
                    <button 
                      type="button" 
                      onClick={() => removeInstallment(idx)} 
                      style={{ ...themedBtn, background: '#ef4444' }}
                    >
                      {t('treatmentsUi.remove')}
                    </button>
                  </div>
                ))}
                {totalInstallments > 0 && (
                  <div className="responsive-row" style={{ marginTop: 8, padding: 10, background: theme.inputBg, borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                    <div>{t('treatmentsUi.installments')}: Bs {totalInstallments.toFixed(2)}</div>
                    {totalInstallments !== calculatedCost && (
                      <div style={{ color: totalInstallments > calculatedCost ? '#ef4444' : '#f59e0b' }}>
                        {t('treatmentsUi.difference')}: Bs {Math.abs(calculatedCost - totalInstallments).toFixed(2)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Form Actions */}
          {submitError && (
            <div role="alert" style={{ padding: 12, borderRadius: 6, background: '#7f1d1d', color: '#fecaca', fontSize: 13 }}>
              {submitError}
            </div>
          )}
          <div className="responsive-row" style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 12 }}>
            <button 
              type="button" 
              onClick={onClose} 
              style={{ ...themedBtn, background: '#6b7280' }}
              disabled={submitting}
            >
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              style={{ ...themedBtn, background: '#667eea' }}
              disabled={submitting || !form.name || (form.isMultiStep && form.steps.length === 0)}
            >
              {submitting
                ? t('common.saving')
                : isEditing
                  ? t('treatmentsUi.updateTreatment')
                  : t('treatmentsUi.createTreatment')}
            </button>
          </div>
        </form>
      </div>
    </ModalBackdrop>
  );
};

export default EnhancedTreatmentModal;

